import React from 'react';

import {useAcceptance} from 'app/acceptance/api';
import {GenericAcceptSidebar} from 'app/acceptance/components/GenericAcceptSidebar';
import {isUserAcceptancePending} from 'app/acceptance/utils';
import {useCurrentUserState} from 'app/current-user/api';
import {SidebarItem} from 'app/general/structures/SidebarItem';
import {getObjectAcceptURL} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import type {
  LeftSidebarElementType,
  LeftSidebarMenuButton,
  LeftSidebarPanelType,
} from 'app/components/general/DetailPage/LeftSidebar/structure/types';
import type {DetailPageItemType} from 'app/components/general/DetailPage/types';

function AcceptancePanelContent<ItemType extends DetailPageItemType>({
  object,
  version,
}: LeftSidebarPanelType<ItemType>) {
  const user = useCurrentUserState().data!;

  if (version == null) {
    return null;
  }
  return (
    <GenericAcceptSidebar
      versionURL={version.url}
      versionApprovedBy={version.approved_by}
      versionApprovedAt={version.approved_at}
      userId={user.id}
      documentId={object.id}
      documentType={object.content_type}
      documentHtmlURL={object.html_url}
      nextReviewDate={object.next_review_at}
    />
  );
}

function AcceptanceMenuButton<ItemType extends DetailPageItemType>({
  object,
  forceTooltipShow,
  hideTooltip,
  location,
}: LeftSidebarMenuButton<ItemType>) {
  const acceptance = useAcceptance({
    object_pk: object.id,
    content_type: object.content_type,
  }).data?.results?.[0];

  const userAcceptanceIsPending = isUserAcceptancePending(object, acceptance);

  return (
    <SidebarItem
      title={
        userAcceptanceIsPending
          ? gt('Your approval was requested')
          : gt('Approval Requested')
      }
      disabled={acceptance == null}
      disabledTooltipText={gt(
        'No approval requests found for this %s',
        getContentType(object.content_type).name
      )}
      icon="far fa-thumbs-up"
      badge={
        userAcceptanceIsPending ? (
          <span className="text-danger font-weight-bold">!</span>
        ) : undefined
      }
      link={
        acceptance != null
          ? getObjectAcceptURL(object, acceptance.object_id, location.search)
          : ''
      }
      forceTooltipShow={forceTooltipShow}
      onClick={hideTooltip}
    />
  );
}

export const AcceptanceSidebar: LeftSidebarElementType<DetailPageItemType> = {
  Panel: AcceptancePanelContent,
  MenuButton: AcceptanceMenuButton,
  type: 'left',
  sidebarName: 'showAccept',
};
